// Solar widget stylesheet (docs/12-solar-roof-scan.md §7.1 and §8).
//
// Added after WIDGET_CSS inside the widget's shadow root, so it reuses the base
// classes (.xq-btn, .xq-input, .xq-title, .xq-sub, .xq-quote-range, .xq-foot …) and
// only adds what the map layout needs. Everything is themed by the installer through
// the --xq-* custom properties: never xenquote's own navy or lime.
//
// Map overlays (pin, rotate handle) are HTML elements Google appends into the map's
// panes, which sit INSIDE the shadow root, so their styles must live here too (the
// spike in §7.2 confirmed this works).

export const SOLAR_CSS = /* css */ `
/* A component rule (e.g. .xq-btn's display) must never beat [hidden] (prototype bug 2) */
[hidden] { display: none !important; }

/* ── Stage: the map fills it, the step card floats over it ─────── */
.xqs { width: var(--xq-width); max-width: 100%; margin: 0 auto; }
.xqs-frame {
  border: var(--xq-canvas-border);
  border-radius: var(--xq-radius);
  box-shadow: var(--xq-shadow);
  overflow: hidden;
  background: var(--xq-bg);
}
.xqs-stage {
  position: relative;
  /* The installer's map height (default SOLAR_DEFAULT_HEIGHT), but never taller than
     the screen, so on a phone the whole map and card stay in view */
  height: min(var(--xqs-height, 760px), 100svh - 32px);
  min-height: 520px;
  container-type: inline-size;
  background: #1b2430;
  overflow: hidden;
}
/* The map library forces position:relative on the map div itself, so absolute positioning
   goes on this wrapper (prototype bug 1: otherwise the map is 0 px tall) */
.xqs-mapwrap, .xqs-map3d { position: absolute; inset: 0; }
.xqs-map { width: 100%; height: 100%; }
.xqs-map3d > * { width: 100%; height: 100%; display: block; }

/* Before the map loads (address step): a static branded panel, so nothing is billed
   for visitors who never start */
.xqs-start {
  position: absolute; inset: 0;
  background:
    radial-gradient(circle at 78% 30%, color-mix(in srgb, var(--xq-accent) 38%, transparent), transparent 42%),
    linear-gradient(160deg, color-mix(in srgb, var(--xq-primary) 88%, #000) 0%, var(--xq-primary) 100%);
}
.xqs-start svg { position: absolute; right: 6%; bottom: 10%; width: min(46%, 360px); height: auto; opacity: 0.9; }

/* ── The step card ─────────────────────────────────────────────── */
.xqs-card {
  position: absolute;
  z-index: 2;
  top: var(--xq-pad);
  left: var(--xq-pad);
  width: 400px;
  max-height: calc(100% - 2 * var(--xq-pad));
  overflow-y: auto;
  padding: var(--xq-pad);
  background: var(--xq-bg);
  border-radius: var(--xq-radius);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.28);
}
.xqs-card .xq-title { font-size: 22px; }
.xqs-eyebrow {
  margin: 0 0 6px; font-size: 13px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.18em; color: var(--xq-muted);
}
.xqs-link {
  display: inline-block; margin-top: 12px; padding: 0; border: none; background: none;
  font: inherit; font-size: 14px; color: var(--xq-muted); text-decoration: underline; cursor: pointer;
}
.xqs-hint {
  margin: 0 0 12px; padding: 10px 12px; border-radius: var(--xq-btn-radius); font-size: 13px; line-height: 1.45;
  background: color-mix(in srgb, var(--xq-accent) 22%, var(--xq-bg)); color: var(--xq-text);
}
.xqs-note { margin: 0 0 14px; font-size: 13px; color: var(--xq-muted); line-height: 1.45; }
.xqs-readout {
  margin: 0 0 14px; padding: 10px 12px; border-left: 4px solid var(--xq-accent);
  border-radius: var(--xq-btn-radius); background: var(--xq-input-bg); font-size: 14px; font-weight: 700;
}
.xqs-actions { display: flex; flex-direction: column; gap: 0; margin-top: 4px; }
.xqs-actions .xq-btn-ghost { margin-top: 10px; }
.xqs-manual { margin-top: 12px; }
.xqs-manual .xq-field { margin-bottom: 12px; }

/* Scan checklist: hollow ring (pending), spinning ring (active), filled tick (done) */
.xqs-checklist { list-style: none; margin: 4px 0 0; padding: 0; display: grid; gap: 12px; }
.xqs-checklist li { display: flex; align-items: center; gap: 12px; font-size: 14px; color: var(--xq-muted); }
.xqs-checklist li::before {
  content: ""; flex: none; width: 20px; height: 20px; border-radius: 50%;
  border: 2px solid var(--xq-border); box-sizing: border-box;
}
.xqs-checklist li.is-active { color: var(--xq-text); font-weight: 600; }
.xqs-checklist li.is-active::before {
  border-color: var(--xq-primary); border-top-color: transparent; animation: xqs-spin 0.8s linear infinite;
}
.xqs-checklist li.is-done { color: var(--xq-text); }
.xqs-checklist li.is-done::before {
  border: none; background: var(--xq-primary)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 12.5 10 16.5 18 8' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    center / 14px no-repeat;
}
@keyframes xqs-spin { to { transform: rotate(360deg); } }

/* Result: slider, stat tiles, usage, battery, price box */
.xqs-summary { margin: 0 0 14px; font-size: 13px; color: var(--xq-muted); line-height: 1.45; }
.xqs-slider { margin: 0 0 14px; padding: 14px; border-radius: var(--xq-radius); background: var(--xq-input-bg); border: 1px solid var(--xq-border); }
.xqs-slider-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 8px; font-size: 15px; }
.xqs-slider input[type="range"] { width: 100%; accent-color: var(--xq-primary); }
.xqs-slider-ends { display: flex; justify-content: space-between; font-size: 12px; color: var(--xq-muted); }
.xqs-badge {
  padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: var(--xq-accent); color: var(--xq-accent-text);
}
.xqs-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin: 0 0 12px; }
.xqs-stat { padding: 10px 8px; border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius); text-align: center; }
.xqs-stat b { display: block; font-size: 18px; line-height: 1.2; font-variant-numeric: tabular-nums; }
.xqs-stat span { display: block; margin-top: 2px; font-size: 11px; color: var(--xq-muted); }
.xqs-usage { margin: 0 0 12px; font-size: 13px; color: var(--xq-muted); }
.xqs-usage summary { cursor: pointer; }
.xqs-usage summary b { text-decoration: underline; color: var(--xq-text); font-weight: 600; }
.xqs-usage .xq-row { margin-top: 8px; }
.xqs-toggle { display: flex; align-items: center; gap: 10px; margin: 0 0 14px; font-size: 14px; cursor: pointer; }
.xqs-toggle input { width: 18px; height: 18px; accent-color: var(--xq-primary); }
.xqs-price { margin: 0 0 14px; padding: 16px; border: 2px solid var(--xq-primary); border-radius: var(--xq-radius); }
.xqs-price-label { font-size: 13px; font-weight: 700; color: var(--xq-muted); }
.xqs-price-kit { margin-top: 6px; font-size: 13px; }
.xqs-price-note { margin-top: 6px; font-size: 12px; color: var(--xq-muted); }

/* ── Status chip over the map ──────────────────────────────────── */
.xqs-chip {
  position: absolute; z-index: 3; top: 14px; left: 50%; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; border-radius: 999px;
  background: color-mix(in srgb, var(--xq-primary) 92%, transparent); color: var(--xq-primary-text);
  font-size: 13px; font-weight: 600; white-space: nowrap; box-shadow: 0 8px 22px rgba(0, 0, 0, 0.3);
}
.xqs-chip-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--xq-accent); animation: xqs-pulse 1.2s ease-in-out infinite; }
@keyframes xqs-pulse { 50% { opacity: 0.35; transform: scale(0.75); } }

/* ── Map overlays (appended into the map's panes, inside this shadow root) ── */
.xqs-pin {
  position: absolute; width: 40px; height: 40px; margin: -46px 0 0 -20px;
  border-radius: 50% 50% 50% 0; transform: rotate(-45deg);
  background: var(--xqs-accent, #E1EE2B); border: 3px solid #FFFFFF; box-shadow: 0 6px 14px rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center; pointer-events: none;
}
.xqs-pin span { transform: rotate(45deg); font-size: 18px; line-height: 1; color: var(--xqs-accent-text, #202937); }
.xqs-pin.is-dropping { animation: xqs-drop 0.55s cubic-bezier(0.3, 1.6, 0.6, 1); }
@keyframes xqs-drop { from { translate: 0 -60px; } to { translate: 0 0; } }
.xqs-rotate {
  position: absolute; width: 30px; height: 30px; margin: -15px 0 0 -15px; border-radius: 50%;
  background: var(--xqs-accent, #E1EE2B); border: 3px solid #FFFFFF; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  color: var(--xqs-accent-text, #202937); font-size: 16px; line-height: 24px; text-align: center;
  cursor: grab; touch-action: none; user-select: none; -webkit-user-select: none;
}
.xqs-rotate.dragging { cursor: grabbing; transform: scale(1.1); }

/* ── Footer ────────────────────────────────────────────────────── */
.xqs .xq-foot { background: var(--xq-bg); }

/* ── Narrow (under 720 px): the card becomes a bottom sheet ────── */
@container (max-width: 719px) {
  .xqs-card {
    top: auto; left: 0; right: 0; bottom: 0; width: auto; max-height: 62%;
    border-radius: var(--xq-radius) var(--xq-radius) 0 0;
  }
  .xqs-chip { top: 10px; max-width: calc(100% - 24px); overflow: hidden; text-overflow: ellipsis; }
}
@container (max-width: 420px) {
  .xqs-card .xq-title { font-size: 20px; }
  .xqs-stat b { font-size: 16px; }
}

/* ── Floating launcher: opens the same layout as a full-screen sheet ── */
.xqs-sheet {
  position: fixed; inset: 0; z-index: 2147483000; display: flex; flex-direction: column;
  background: rgba(15, 23, 42, 0.55); padding: 16px;
}
.xq-contained .xqs-sheet { position: absolute; }
.xqs-sheet .xqs { width: 100%; height: 100%; display: flex; flex-direction: column; }
.xqs-sheet .xqs-frame { flex: 1; display: flex; flex-direction: column; }
.xqs-sheet .xqs-stage { flex: 1; height: auto; }
.xqs-sheet-close {
  position: absolute; z-index: 4; top: 10px; right: 10px; width: 36px; height: 36px; border: none; border-radius: 50%;
  background: var(--xq-bg); color: var(--xq-text); font-size: 20px; cursor: pointer; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
`;
